/**
 * Brand Loading Indicator — Alpine.js plugin
 *
 * Automatically replaces Filament's default spinning circle with the
 * animated brand logo (equalizer bars) during Livewire requests.
 *
 * Also shows a global floating indicator at the bottom of the page
 * when any Livewire request is in-flight (after a short delay to
 * avoid flash on fast responses).
 */

const BRAND_LOADER_SVG = `<svg viewBox="0 0 36 36" class="fi-icon brand-loader brand-loader--inline" aria-hidden="true">
    <rect x="1" y="28" width="6" height="8" rx="1.5" class="brand-loader-bar brand-loader-bar-1"/>
    <rect x="9" y="22" width="6" height="14" rx="1.5" class="brand-loader-bar brand-loader-bar-2"/>
    <rect x="17" y="14" width="6" height="22" rx="1.5" class="brand-loader-bar brand-loader-bar-3"/>
    <rect x="25" y="6" width="6" height="30" rx="1.5" class="brand-loader-bar brand-loader-bar-4"/>
    <path d="M26 4.5 L28 1.5 L30 4.5" fill="none" stroke-width="1.5"
          stroke-linecap="round" stroke-linejoin="round"
          class="brand-loader-chevron"/>
</svg>`;

/**
 * Replace all visible Filament loading-indicator SVGs with brand loader.
 * Uses a MutationObserver to catch dynamically inserted indicators.
 */
function initLoadingIndicatorReplacer() {
    function replaceIndicator(el) {
        if (el.dataset.brandReplaced) return;
        el.dataset.brandReplaced = '1';

        const wrapper = document.createElement('span');
        wrapper.innerHTML = BRAND_LOADER_SVG;
        const svg = wrapper.firstElementChild;

        // Copy sizing classes from original
        for (const cls of el.classList) {
            if (cls.startsWith('fi-size-') || cls.startsWith('h-') || cls.startsWith('w-')) {
                svg.classList.add(cls);
            }
        }

        // Copy wire:loading / x-show attributes so visibility logic is preserved
        for (const attr of el.attributes) {
            if (attr.name.startsWith('wire:') || attr.name.startsWith('x-') || attr.name === 'style') {
                svg.setAttribute(attr.name, attr.value);
            }
        }

        el.replaceWith(svg);
    }

    function scanAndReplace(root = document) {
        root.querySelectorAll('.fi-loading-indicator:not([data-brand-replaced])').forEach(replaceIndicator);
    }

    // Initial scan
    scanAndReplace();

    // Observe DOM for dynamically added indicators (modals, dropdowns, etc.)
    const observer = new MutationObserver((mutations) => {
        for (const mutation of mutations) {
            for (const node of mutation.addedNodes) {
                if (node.nodeType !== 1) continue;
                if (node.classList?.contains('fi-loading-indicator')) {
                    replaceIndicator(node);
                } else if (node.querySelectorAll) {
                    scanAndReplace(node);
                }
            }
        }
    });

    observer.observe(document.body, { childList: true, subtree: true });
}

/**
 * Global floating loader — shown at bottom-right when Livewire is working.
 * Stays out of the way of Filament notifications (top-right).
 */
function initGlobalLoader() {
    // Create the loader element
    const loader = document.createElement('div');
    loader.id = 'brand-global-loader';
    loader.innerHTML = `
        <svg viewBox="0 0 36 36" class="h-5 w-5 brand-loader" aria-hidden="true">
            <rect x="1" y="28" width="6" height="8" rx="1.5" class="brand-loader-bar brand-loader-bar-1"/>
            <rect x="9" y="22" width="6" height="14" rx="1.5" class="brand-loader-bar brand-loader-bar-2"/>
            <rect x="17" y="14" width="6" height="22" rx="1.5" class="brand-loader-bar brand-loader-bar-3"/>
            <rect x="25" y="6" width="6" height="30" rx="1.5" class="brand-loader-bar brand-loader-bar-4"/>
            <path d="M26 4.5 L28 1.5 L30 4.5" fill="none" stroke-width="1.5"
                  stroke-linecap="round" stroke-linejoin="round"
                  class="brand-loader-chevron"/>
        </svg>
        <span class="brand-global-loader-text">Chargement…</span>
    `;
    document.body.appendChild(loader);

    let activeRequests = 0;
    let showTimeout = null;

    function show() {
        if (showTimeout) return;
        showTimeout = setTimeout(() => {
            loader.classList.add('brand-global-loader--visible');
        }, 300);
    }

    function hide() {
        if (showTimeout) {
            clearTimeout(showTimeout);
            showTimeout = null;
        }
        loader.classList.remove('brand-global-loader--visible');
    }

    // Hook into Livewire's commit lifecycle
    if (window.Livewire) {
        Livewire.hook('commit', ({ succeed, fail }) => {
            activeRequests++;
            if (activeRequests > 0) show();

            succeed(() => {
                activeRequests = Math.max(0, activeRequests - 1);
                if (activeRequests === 0) hide();
            });

            fail(() => {
                activeRequests = Math.max(0, activeRequests - 1);
                if (activeRequests === 0) hide();
            });
        });
    }
}

/**
 * Animate the topbar brand logo during Livewire requests.
 * Adds .brand-logo--active to the SVG while requests are in-flight.
 */
function initTopbarLogoAnimation() {
    const logoSvg = document.getElementById('brand-logo-svg');
    if (!logoSvg) return;

    let activeRequests = 0;
    let activateTimeout = null;

    function activate() {
        if (activateTimeout) return;
        // Small delay — don't animate on instant responses
        activateTimeout = setTimeout(() => {
            logoSvg.classList.add('brand-logo--active');
        }, 200);
    }

    function deactivate() {
        if (activateTimeout) {
            clearTimeout(activateTimeout);
            activateTimeout = null;
        }
        logoSvg.classList.remove('brand-logo--active');
    }

    if (window.Livewire) {
        Livewire.hook('commit', ({ succeed, fail }) => {
            activeRequests++;
            if (activeRequests > 0) activate();

            succeed(() => {
                activeRequests = Math.max(0, activeRequests - 1);
                if (activeRequests === 0) deactivate();
            });

            fail(() => {
                activeRequests = Math.max(0, activeRequests - 1);
                if (activeRequests === 0) deactivate();
            });
        });
    }
}

/**
 * Animate the topbar brand logo when background jobs are running.
 * Listens for Echo events on the organization channel.
 * Uses a slower animation (.brand-logo--job) to distinguish from loading.
 */
function initJobLogoAnimation() {
    const logoSvg = document.getElementById('brand-logo-svg');
    if (!logoSvg || !window.Echo) return;

    // Extract org ID from Filament tenant URL: /app/{slug}/...
    // The Echo channel uses the org ID, which we get from a meta tag or data attribute
    const orgChannel = document.querySelector('meta[name="org-channel"]')?.content;
    if (!orgChannel) return;

    let jobTimeout = null;

    function pulseForJob() {
        // Don't override active loading animation (it takes priority)
        if (logoSvg.classList.contains('brand-logo--active')) return;

        logoSvg.classList.add('brand-logo--job');

        // Auto-stop after 5s — the next event will re-trigger if still running
        clearTimeout(jobTimeout);
        jobTimeout = setTimeout(() => {
            logoSvg.classList.remove('brand-logo--job');
        }, 5000);
    }

    Echo.private(orgChannel)
        .listen('.WorkflowRunUpdated', () => pulseForJob());
}

/**
 * Init everything once Livewire is ready.
 */
document.addEventListener('livewire:init', () => {
    initLoadingIndicatorReplacer();
    initGlobalLoader();
    initTopbarLogoAnimation();
    initJobLogoAnimation();
});
